import React, { Fragment } from 'react';
import { observer } from 'mobx-react';
import { BaasicInput, Loader, BaasicDropdown, BaasicButton, BaasicTable, InfoPanel } from 'core/components';
import { fulfillmentOrderStatus } from 'common/constants';
import { defaultTemplate } from 'core/hoc';
import { DeletePalletDropdownAction } from 'core/components/select';
import Barcode from 'react-barcode';
import _ from 'lodash';
import { translate } from 'core/utils';
import { isSome } from 'core/utils';

const LabelingModeTemplate = ({
	t,
	labelingModeViewStore,
	labelingModeViewStore: {
		setInputs,
		onUpcScan,
		isUpcScanned,
		isFulfillmentOrderIdEntered,
		scanUpcMessage,
		scanUpcMessageData,
		steps,
		steps: { labelingStep },
		loading,
		activeStep,
		useRegularPalletizingMode,
		isLtlPackingMode,
	},
}) => {
	if (loading) return <Loader />;
	return (
		<div className="ReactModal__form">
			<div className="ReactModal__header">
				<h3 className="spc--bottom--sml">
					{t(isLtlPackingMode ? 'FULFILLMENTORDER.LABELINGMODE.LTLTITLE' : 'FULFILLMENTORDER.LABELINGMODE.TITLE')}
				</h3>
			</div>
			<div className="ReactModal__body">
				{!useRegularPalletizingMode && !isLtlPackingMode && <CombinedLabelingAndPalletizing viewStore={labelingModeViewStore} t={t} />}
				{(activeStep.number === 1 || activeStep.number === 2 || !isUpcScanned) && (useRegularPalletizingMode || isLtlPackingMode) && (
					<Fragment>
						<div className="col col-sml-5 col-med-5">
							{isLtlPackingMode && !isFulfillmentOrderIdEntered && (
								<BaasicInput autoFocus={true} field={labelingStep.form.$('fulfillmentOrderId')} onKeyUp={onUpcScan} />
							)}
							{!isLtlPackingMode && (
								<BaasicInput
									autoFocus={true}
									field={labelingStep.form.$('upc')}
									reference={setInputs}
									onKeyUp={onUpcScan}
									disabled={!useRegularPalletizingMode && palletDropdownStore.value == null}
								/>
							)}
						</div>
						{scanUpcMessage && scanUpcMessage.length != 0 && (
							<p className={'type--color--warning'}>{translate([scanUpcMessage, scanUpcMessageData])}</p>
						)}
					</Fragment>
				)}
				{(isUpcScanned || isFulfillmentOrderIdEntered) && (
					<Fragment>
						{/* STEPS HEADER */}
						<Header {...{ steps, t, labelingModeViewStore }} />
						{/* STEPS */}
						{renderSteps(labelingModeViewStore, t)}
						{/* FOOTER */}
						<Footer labelingModeViewStore={labelingModeViewStore} t={t} />
					</Fragment>
				)}
			</div>
		</div>
	);
};

const renderSteps = (labelingModeViewStore, t) => {
	switch (labelingModeViewStore.activeStep.number) {
		case 1:
			return <SelectingFoStep labelingModeViewStore={labelingModeViewStore} t={t} />;
		case 2:
			return <LabelingStep labelingModeViewStore={labelingModeViewStore} t={t} />;
		case 3:
			return <DimensionsStep labelingModeViewStore={labelingModeViewStore} t={t} />;
		case 4:
			return <FinalStep labelingModeViewStore={labelingModeViewStore} t={t} />;
		default:
			return null;
	}
};

const CombinedLabelingAndPalletizing = observer(({ viewStore, t }) => {
	const {
		palletDropdownStore,
		togglePallet,
		steps: { labelingStep },
		setInputs,
		onUpcScan,
		scanUpcMessage,
		scanUpcMessageData,
		data,
		tableData,
		onPrintLabel,
		isUpcScanned,
		createPalletMode,
		palletDeleteConfirmModal,
		displayDeleteAction,
		onDeletePallet,
		warehouseDropdownStore,
		isSmartshipWarehouseAdministrator,
		isWarehouse,
		isLtlPackingMode,
		isFulfillmentOrderIdEntered,
	} = viewStore;
	return (
		<Fragment>
			{createPalletMode && <PalletCreate viewStore={viewStore} t={t} />}
			{!isUpcScanned && !isFulfillmentOrderIdEntered && !createPalletMode && (
				<Fragment>
					{isSmartshipWarehouseAdministrator && (
						<div className="row">
							<div className="col col-sml-6 col-med-6">
								<BaasicDropdown
									dropdownStore={warehouseDropdownStore}
									className="spc--bottom--sml flex--grow--1"
									autoFocus
									label="FULFILLMENTORDER.PALLETIZINGMODE.WAREHOUSE"
								/>
							</div>
						</div>
					)}
					<div className="row">
						<div className="col col-sml-6 col-med-6">
							<div className="inputgroup spc--bottom--sml">
								<div className="inputgroup--main">
									<BaasicDropdown
										autoFocus
										dropdownStore={palletDropdownStore}
										optionOverride={{
											shouldDisplayCallback: value => displayDeleteAction(value),
											Component: DeletePalletDropdownAction,
											onClick: value => onDeletePallet(value),
										}}
										label="FULFILLMENTORDER.PALLETIZINGMODE.PALLET"
									/>
								</div>
								<div className="inputgroup--aside align--v--bottom">
									<BaasicButton
										className="btn btn--med btn--primary spc--left--sml"
										onClick={() => togglePallet()}
										type="button"
										label="FULFILLMENTORDER.PALLETIZINGMODE.NEWPALLET"
										disabled={!isWarehouse && !warehouseDropdownStore.value}
									/>
								</div>
							</div>
						</div>
						<div className="col col-sml-6 col-med-6">
							{isLtlPackingMode && !isFulfillmentOrderIdEntered && (
								<BaasicInput
									autoFocus={true}
									field={labelingStep.form.$('fulfillmentOrderId')}
									onKeyUp={onUpcScan}
									disabled={palletDropdownStore.value == null}
								/>
							)}
							{!isLtlPackingMode && (
								<BaasicInput
									autoFocus={true}
									field={labelingStep.form.$('upc')}
									reference={setInputs}
									onKeyUp={onUpcScan}
									disabled={palletDropdownStore.value == null}
								/>
							)}
						</div>
					</div>
					{scanUpcMessage && scanUpcMessage.length != 0 && (
						<p className={'type--color--warning'}>{translate([scanUpcMessage, scanUpcMessageData])}</p>
					)}
					{data.length > 0 && !isUpcScanned && !isFulfillmentOrderIdEntered && (
						<div className="row">
							<div className="col col-sml-12 col-med-12 separator--primary spc--bottom--med">
								<BaasicTable
									{...{
										tableData,
										viewStore,
										actions: viewStore,
									}}
									data={data.slice()}
								/>
							</div>
						</div>
					)}
					<div className="row">
						<div className="col col-sml-4 col-med-4">
							<BaasicButton
								className="btn btn--med btn--primary spc--bottom--sml"
								type="button"
								label="PRINTPALLETLABEL"
								onClick={() => onPrintLabel(palletDropdownStore.value.extra.label)}
								disabled={palletDropdownStore.value == null || palletDropdownStore.value.extra.label === ''}
								icon="fa fa-print spc--right--tny"
							/>
						</div>
					</div>
				</Fragment>
			)}
		</Fragment>
	);
});

const PalletCreate = observer(({ viewStore, t }) => {
	const {
		palletCreateForm,
		barcode,
		setRef,
		carrierTypeServiceTypeDropdownStore,
		createPalletNotification,
		togglePallet,
	} = viewStore;
	return (
		<Fragment>
			<form className="ReactModal__form">
				<div className="ReactModal__body">
					<div className="spc--bottom--sml type--center">
						<Barcode format={'CODE39'} value={barcode} ref={ref => setRef(ref)} renderer={'img'} />
					</div>
					<div className="spc--bottom--sml">
						<BaasicInput field={palletCreateForm.$('name')} />
					</div>
					{createPalletNotification && createPalletNotification.includes('CONFLICT') && (
						<p className="type--color--warning spc--bottom--sml">{t(createPalletNotification)}</p>
					)}
					<BaasicDropdown
						dropdownStore={carrierTypeServiceTypeDropdownStore}
						className="spc--bottom--sml flex--grow--1"
					/>
				</div>
				{palletCreateForm.error != null && palletCreateForm.error}
				<div className="ReactModal__footer">
					<BaasicButton
						className="btn btn--med btn--primary spc--bottom--sml spc--right--sml"
						type="submit"
						onClick={palletCreateForm.onSubmit}
						label="SAVE"
					/>
					<BaasicButton
						className="btn btn--med btn--ghost spc--bottom--sml"
						type="button"
						onClick={togglePallet}
						label="BACK"
					/>
				</div>
			</form>
		</Fragment>
	);
});

const SelectingFoStep = ({
	labelingModeViewStore: {
		fulfillmentOrders,
		selectFulfillmentOrder,
		packagedFulfillmentOrders,
		product,
		handleShowAll,
	},
	t,
}) => {
	return (
		<Fragment>
			<div className="card card--shadow card--med spc--bottom--sml--alt">
				<div className="labeling__product">
					<div className="flex--grow--1">
						<div className="spc--bottom--sml--alt">
							<label className="type--xsml type--grey-5 type--uppercase">
								{t('FULFILLMENTORDER.LABELINGMODE.SELECTINGFOSTEP.PRODUCTSKU')}
							</label>
							<p>{product.mainSku}</p>
						</div>
						<div>
							<label className="type--xsml type--grey-5 type--uppercase">
								{t('FULFILLMENTORDER.LABELINGMODE.SELECTINGFOSTEP.PRODUCTNAME')}
							</label>
							<p>{product.name}</p>
						</div>
					</div>
					<div className="labeling__product__preview">
						<img src="https://milliardbedding.com/wp-content/uploads/2021/04/51104088640_40f294f443_o-scaled-700x700.jpg"></img>
					</div>
				</div>
			</div>
			<label className="labeling__subtitle spc--bottom--sml">
				{t('FULFILLMENTORDER.LABELINGMODE.ASSOCIATEDORDERS')}
			</label>
			{_.map(fulfillmentOrders, fulfillmentOrder => {
				return (
					<div key={fulfillmentOrder.fulfillmentOrderId} className="labeling__list">
						<TableHeader fulfillmentOrder={fulfillmentOrder} selectFulfillmentOrder={selectFulfillmentOrder} t={t} />
						<div className="labeling__list__body">
							<div className={`labeling__list__table__wrapper ` + (fulfillmentOrder.showAll ? '' : 'not-expanded')}>
								<table className="labeling__list__table">
									<thead>
										<tr>
											<th>{t('FULFILLMENTORDER.LABELINGMODE.SKU')}:</th>
											<th>{t('FULFILLMENTORDER.LABELINGMODE.QUANTITY')}:</th>
										</tr>
									</thead>
									<tbody>
										{_.map(fulfillmentOrder.fulfillmentOrderItems, item => {
											return (
												<tr key={item.id}>
													<td>{item.sku}</td>
													<td>{item.quantity}</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
							<div
								className={
									fulfillmentOrder.fulfillmentOrderItems.length <= 3 || fulfillmentOrder.showAll
										? 'display--none'
										: `type--center spc--top--sml`
								}
							>
								<button className="btn btn--sml btn--ghost" onClick={() => handleShowAll(fulfillmentOrder.id)}>
									{t('FULFILLMENTORDER.LABELINGMODE.SHOWALL')}
									<i className="icon icon--sml icon--arrow-down spc--left--tny"></i>
								</button>
							</div>
						</div>
					</div>
				);
			})}
			{/* LAST PACKAGED FULFILLMENT ORDERS */}
			{packagedFulfillmentOrders.length > 0 && (
				<Fragment>
					<label className="labeling__subtitle spc--bottom--sml">
						{t('FULFILLMENTORDER.LABELINGMODE.LASTPACKAGED')}
					</label>
					<ProcessedFulfillmentOrders fulfillmentOrders={packagedFulfillmentOrders} t={t} />
				</Fragment>
			)}
		</Fragment>
	);
};

const LabelingStep = observer(
	({
		labelingModeViewStore: {
			selectedFulfillmentOrder,
			openConfirmationModal,
			getRemoveItemConfirmModalProps,
			closeBoxMessage,
			boxedFulfillmentOrders,
			newFulfillmentOrderCreated,
		},
		t,
	}) => {
		return (
			<Fragment>
				{/* BOXED FULFILLMENT ORDERS - VISIBLE ONLY IF THE FULFILLMENT ORDER IS SPLITTED */}
				<div className="labeling__list">
					{boxedFulfillmentOrders.length > 0 && (
						<Fragment>
							<label className="labeling__subtitle spc--bottom--sml">
								{t('FULFILLMENTORDER.LABELINGMODE.PROCESSEDFOS')}
							</label>
							<ProcessedFulfillmentOrders fulfillmentOrders={boxedFulfillmentOrders} t={t} />
						</Fragment>
					)}
				</div>
				{/* CURRENTLY PROCESSING FULFILLMENT ORDER */}
				<div className="labeling__list">
					{newFulfillmentOrderCreated && (
						<label className="labeling__subtitle spc--bottom--sml">
							{t('FULFILLMENTORDER.LABELINGMODE.REMAININGSKUS')}
						</label>
					)}
					<TableHeader fulfillmentOrder={selectedFulfillmentOrder} t={t} />
					<div className="labeling__list__body">
						<table className="labeling__list__table">
							<thead>
								<tr>
									<th>{t('FULFILLMENTORDER.LABELINGMODE.SCANNED')}</th>
									<th>{t('FULFILLMENTORDER.LABELINGMODE.SKU')}</th>
									<th>{t('FULFILLMENTORDER.LABELINGMODE.QUANTITY')}</th>
									<th className="type--right">{t('FULFILLMENTORDER.LABELINGMODE.ACTIONS')}</th>
								</tr>
							</thead>
							<tbody>
								{_.map(selectedFulfillmentOrder.fulfillmentOrderItems, item => {
									const quantityLeft = item.scannedQuantity === 0 ? null : `${item.scannedQuantity}/${item.quantity}`;
									return (
										<tr key={item.id}>
											<td>
												<div className={'scan ' + (item.isScanned ? 'is-scanned' : '')}>
													<div className={' ' + (item.isScanned ? 'is-scanned' : '')} value={item} key={item.id}>
														{quantityLeft}
													</div>
												</div>
											</td>
											<td>{item.sku}</td>
											<td>{item.quantity}</td>
											{item.scannedQuantity > 0 ? (
												<td className="type--right" title={t('FULFILLMENTORDER.BULKPRINT.REMOVE')}>
													<i
														className="icon icon--med icon--unscan cursor--pointer"
														onClick={() => openConfirmationModal(getRemoveItemConfirmModalProps(item.product.id))}
													></i>
												</td>
											) : null}
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
					{closeBoxMessage && (
						<div className="col col-sml-12 spc--bottom--sml">
							<p className={'type--color--warning'}>{t(closeBoxMessage)}</p>
						</div>
					)}
				</div>
			</Fragment>
		);
	}
);

const DimensionsStep = observer(
	({
		labelingModeViewStore: {
			selectedFulfillmentOrder,
			createLabelMessage,
			steps: { dimensionsStep },
			showCreateTemplate,
			onCreateTemplateCancel,
			newFulfillmentOrderCreated,
			dimensionsStepErrorMessage,
			boxDropdownStore,
			isLtlLabeling,
			selectedFoHasUploadedBol,
			removePallet,
			unscanContent,
			markWeightOverridden,
			palletReconciliation,
			openPallet,
			onPalletUpcScan,
			palletScanMessage,
			setInputs,
			closePallet,
			reopenPallet,
			isPalletReadyToClose,
		},
		t,
	}) => {
		return (
			<Fragment>
				{newFulfillmentOrderCreated && (
					<div className="message message--tny message--primary message--primary--info spc--bottom--sml--alt">
						<p>{t('FULFILLMENTORDER.LABELINGMODE.NEWFULFILLMENTORDERCREATED')}</p>
					</div>
				)}
				<div className="labeling__list">
					<TableHeader fulfillmentOrder={selectedFulfillmentOrder} t={t} />
				</div>
				{isLtlLabeling ? (
					selectedFoHasUploadedBol ? (
						<InfoPanel messages={[t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.BOLALREADYUPLOADED')]} />
					) : (
						<PalletDimensions
							dimensionsStep={dimensionsStep}
							disabled={dimensionsStepErrorMessage}
							openPallet={openPallet}
							onPalletUpcScan={onPalletUpcScan}
							palletScanMessage={palletScanMessage}
							setInputs={setInputs}
							closePallet={closePallet}
							reopenPallet={reopenPallet}
							removePallet={removePallet}
							unscanContent={unscanContent}
							markWeightOverridden={markWeightOverridden}
							isPalletReadyToClose={isPalletReadyToClose}
							palletReconciliation={palletReconciliation}
							t={t}
						/>
					)
				) : (
					<div className="row">
						<div className="col col-sml-12 col-med-4 spc--bottom--sml">
							<BaasicDropdown
								dropdownStore={boxDropdownStore}
								isClearable={true}
								className="flex--grow--1"
								disabled={dimensionsStepErrorMessage}
							/>
						</div>
					</div>
				)}
				{dimensionsStepErrorMessage && (
					<p className={'type--color--warning spc--bottom--sml'}>{t(dimensionsStepErrorMessage)}</p>
				)}
				{!isLtlLabeling && showCreateTemplate && <BoxCreate {...{ dimensionsStep, onCreateTemplateCancel, t }} />}
				{createLabelMessage && createLabelMessage.length != 0 && (
					<p className={'type--color--warning spc--bottom--sml'}>{t(createLabelMessage)}</p>
				)}
			</Fragment>
		);
	}
);

const PalletDimensions = observer(
	({
		dimensionsStep,
		disabled,
		openPallet,
		onPalletUpcScan,
		palletScanMessage,
		setInputs,
		closePallet,
		reopenPallet,
		removePallet,
		unscanContent,
		markWeightOverridden,
		isPalletReadyToClose,
		palletReconciliation,
		t,
	}) => {
		const pallets = dimensionsStep.form.$('pallets');
		return (
			<div className="box--primary box--sml spc--bottom--sml">
				<div className="row">
					<div className="col col-sml-12 col-med-6 spc--bottom--sml">
						<BaasicInput
							autoFocus={true}
							field={dimensionsStep.form.$('palletUpc')}
							reference={setInputs}
							onKeyUp={onPalletUpcScan}
							disabled={disabled || !openPallet}
						/>
					</div>
				</div>
				{palletScanMessage && <p className="type--color--warning spc--bottom--sml">{t(palletScanMessage)}</p>}

				<h4 className="type--base type--wgt--bold type--color--grey-5 spc--bottom--sml">
					{t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.ITEMS')}
				</h4>

				{/* Per-product "N left": units still to scan onto a pallet (0 = fully placed, shown green). */}
				<div className="spc--bottom--sml">
					{palletReconciliation.map(row => (
						<div
							key={row.fulfillmentOrderItemId}
							className={`flex--primary spc--bottom--tny ${
								row.ok ? 'type--color--success' : 'type--color--warning'
							}`}
						>
							<span className="type--wgt--bold spc--right--sml" style={{ minWidth: '110px' }}>
								{row.sku}
							</span>
							<span>
								{row.left} {t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.LEFT')}
							</span>
						</div>
					))}
				</div>

				<h4 className="type--base type--wgt--bold type--color--grey-5 spc--top--sml spc--bottom--med">
					{t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.PALLETS')}
				</h4>

				{pallets.map((pallet, index) => {
					const isClosed = pallet.$('isClosed').value === true;
					const placed = pallet
						.$('contents')
						.map(content => content)
						.filter(content => (Number(content.$('quantity').value) || 0) > 0);
					return (
						<div key={pallet.path} className="box--primary box--sml spc--bottom--sml">
							<div className="flex--secondary spc--bottom--sml">
								<label className="type--color--grey-5">
									{t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.PALLET')} {index + 1} -{' '}
									{t(
										isClosed
											? 'FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.CLOSED'
											: 'FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.OPEN'
									)}
								</label>
								<div>
									{isClosed && (
										<BaasicButton
											className="btn btn--sml btn--ghost spc--right--sml"
											type="button"
											label="FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.REOPENPALLET"
											onClick={() => reopenPallet(pallet)}
											disabled={disabled}
										/>
									)}
									{/* A shipment must keep at least one pallet, so delete shows only when there is more than one. */}
									{pallets.value.length > 1 && (
										<BaasicButton
											className="btn btn--sml btn--ghost btn--icon type--color--warning"
											type="button"
											onlyIcon={true}
											icon="fa fa-trash"
											title="FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.REMOVEPALLET"
											onClick={() => removePallet(pallet)}
											disabled={disabled}
										/>
									)}
								</div>
							</div>

							{placed.length === 0 && (
								<p className="type--color--grey-5 spc--bottom--tny">
									{t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.EMPTYPALLET')}
								</p>
							)}
							{placed.map(content => (
								<div key={content.path} className="flex--primary spc--bottom--tny">
									<span className="type--wgt--bold spc--right--sml" style={{ minWidth: '110px' }}>
										{content.$('sku').value}
									</span>
									<span className="spc--right--sml">{content.$('quantity').value}</span>
									{!isClosed && !disabled && (
										<i
											className="icon icon--med icon--unscan cursor--pointer"
											title={t('FULFILLMENTORDER.BULKPRINT.REMOVE')}
											onClick={() => unscanContent(pallet, content)}
										></i>
									)}
								</div>
							))}

							{/* Weight is calculated from the scanned contents but can be overridden. */}
							<div className="row row--form spc--top--med">
								<div className="col col-sml-12 col-med-6 col-lrg-3">
									<BaasicInput
										field={pallet.$('palletLength')}
										placeholder={t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.PALLETLENGTH')}
										disabled={disabled || isClosed}
									/>
								</div>
								<div className="col col-sml-12 col-med-6 col-lrg-3">
									<BaasicInput
										field={pallet.$('palletWidth')}
										placeholder={t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.PALLETWIDTH')}
										disabled={disabled || isClosed}
									/>
								</div>
								<div className="col col-sml-12 col-med-6 col-lrg-3">
									<BaasicInput
										field={pallet.$('palletHeight')}
										placeholder={t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.PALLETHEIGHT')}
										disabled={disabled || isClosed}
									/>
								</div>
								<div className="col col-sml-12 col-med-6 col-lrg-3">
									<BaasicInput
										field={pallet.$('palletWeight')}
										placeholder={t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.PALLETWEIGHT')}
										disabled={disabled || isClosed}
										onKeyUp={() => markWeightOverridden(pallet)}
									/>
								</div>
							</div>

							{!isClosed && (
								<div className="type--right spc--top--sml">
									<BaasicButton
										className="btn btn--med btn--primary"
										type="button"
										label="FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.CLOSEPALLET"
										onClick={() => closePallet()}
										disabled={disabled || !isPalletReadyToClose(pallet)}
									/>
								</div>
							)}
						</div>
					);
				})}
			</div>
		);
	}
);

const BoxCreate = ({ dimensionsStep, onCreateTemplateCancel, t }) => {
	return (
		<form className="box--primary box--sml spc--bottom--sml" onSubmit={dimensionsStep.form.onSubmit}>
			<div className="box__header">
				<h4 className="type--base type--color--grey-5">
					{t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.CREATEDIMENSIONSTEMPLATE')}
				</h4>
			</div>
			<div className="row row--form">
				<div className="col col-sml-12 col-med-6 col-lrg-3">
					<BaasicInput
						field={dimensionsStep.form.$('name')}
						placeholder={t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.NAME')}
					/>
				</div>
				<div className="col col-sml-12 col-med-6 col-lrg-3">
					<BaasicInput
						field={dimensionsStep.form.$('height')}
						placeholder={t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.HEIGHT')}
					/>
				</div>
				<div className="col col-sml-12 col-med-6 col-lrg-3">
					<BaasicInput
						field={dimensionsStep.form.$('width')}
						placeholder={t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.WIDTH')}
					/>
				</div>
				<div className="col col-sml-12 col-med-6 col-lrg-3">
					<BaasicInput
						field={dimensionsStep.form.$('length')}
						placeholder={t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.LENGTH')}
					/>
				</div>
			</div>

			<div>
				<button className="btn btn--sml btn--primary spc--right--sml" type="submit">
					{t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.CREATE')}
				</button>
				<button className="btn btn--sml btn--outline" type="button" onClick={() => onCreateTemplateCancel()}>
					{t('FULFILLMENTORDER.LABELINGMODE.DIMENSIONSSTEP.CANCEL')}
				</button>
			</div>
		</form>
	);
};

const FinalStep = observer(
	({
		labelingModeViewStore: {
			setInputs,
			selectedFulfillmentOrder,
			onTrackingNumberScan,
			scanLabelMessage,
			steps: { finalStep },
			createLabelMessage,
		},
		t,
	}) => {
		return (
			<Fragment>
				<div className="labeling__list">
					<TableHeader fulfillmentOrder={selectedFulfillmentOrder} t={t} />
				</div>
				<div className="row">
					<div className="col col-sml-12 col-med-6">
						<BaasicInput
							autoFocus
							field={finalStep.form.$('trackingNumber')}
							reference={setInputs}
							onKeyUp={onTrackingNumberScan}
							disabled={selectedFulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.exitScan}
						/>
						{scanLabelMessage != null && (
							<p
								className={
									scanLabelMessage.includes('NOTFOUND') ||
									scanLabelMessage.includes('STATUSNOTLABELCREATED') ||
									scanLabelMessage.includes('CARRIERMISMATCH')
										? 'type--color--warning spc--bottom--sml'
										: 'type--color--success spc--bottom--sml'
								}
							>
								{t(scanLabelMessage)}
							</p>
						)}
					</div>
				</div>
				{createLabelMessage && createLabelMessage.length != 0 && (
					<p className={'type--color--warning spc--bottom--sml'}>{t(createLabelMessage)}</p>
				)}
			</Fragment>
		);
	}
);

export const TableHeader = observer(({ fulfillmentOrder, selectFulfillmentOrder, t }) => {
	const status =
		fulfillmentOrder.fulfillmentOrderStatus.abrv === fulfillmentOrderStatus.labelCreated
			? 'created'
			: fulfillmentOrder.fulfillmentOrderStatus.abrv === fulfillmentOrderStatus.picking
			? 'picking'
			: fulfillmentOrder.fulfillmentOrderStatus.abrv === fulfillmentOrderStatus.palletized ||
			  fulfillmentOrder.fulfillmentOrderStatus.abrv === fulfillmentOrderStatus.packaged ||
			  fulfillmentOrder.fulfillmentOrderStatus.abrv === fulfillmentOrderStatus.exitScan
			? 'final'
			: '';
	return (
		<div
			className={`labeling__list__header ` + status + (selectFulfillmentOrder ? ' is-clickable' : '')}
			key={fulfillmentOrder.id}
			onClick={selectFulfillmentOrder ? () => selectFulfillmentOrder(fulfillmentOrder) : null}
		>
			<div>
				<label>{t('FULFILLMENTORDER.LABELINGMODE.ID')}</label>
				<p>{fulfillmentOrder.fulfillmentOrderId}{fulfillmentOrder.isLtlShipment && <i className="icon icon--med icon--ltl spc--left--tny" title="LTL" />}</p>
			</div>
			<div>
				<label>{t('FULFILLMENTORDER.LABELINGMODE.STATUS')}</label>
				<p className={`labeling__status ` + status}>{fulfillmentOrder.fulfillmentOrderStatus.name}</p>
			</div>
			<div>
				<label>{t('FULFILLMENTORDER.LABELINGMODE.MPO')}</label>
				<p>{fulfillmentOrder.order.orderNumber}</p>
			</div>
		</div>
	);
});

const ProcessedFulfillmentOrders = ({ fulfillmentOrders, t }) => {
	return (
		<Fragment>
			{_.map(fulfillmentOrders, fulfillmentOrder => {
				return (
					<div className="labeling__list">
						<TableHeader fulfillmentOrder={fulfillmentOrder} t={t} />
						<div className="labeling__list__body">
							<div className={`labeling__list__table__wrapper ` + (fulfillmentOrder.showAll ? '' : 'not-expanded')}>
								<table className="labeling__list__table">
									<thead>
										<tr>
											<th>{t('FULFILLMENTORDER.LABELINGMODE.SKU')}:</th>
											<th>{t('FULFILLMENTORDER.LABELINGMODE.QUANTITY')}:</th>
										</tr>
									</thead>
									<tbody>
										{_.map(fulfillmentOrder.fulfillmentOrderItems, item => {
											return (
												<tr key={item.id}>
													<td>{item.sku}</td>
													<td>{item.quantity}</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
							<div
								className={
									fulfillmentOrder.fulfillmentOrderItems.length <= 3 || fulfillmentOrder.showAll
										? 'display--none'
										: `type--center spc--top--sml`
								}
							>
								<button className="btn btn--sml btn--ghost" onClick={() => handleShowAll(fulfillmentOrder.id, true)}>
									{t('FULFILLMENTORDER.LABELINGMODE.SHOWALL')}
									<i className="icon icon--sml icon--arrow-down spc--left--tny"></i>
								</button>
							</div>
						</div>
					</div>
				);
			})}
		</Fragment>
	);
};

const Footer = observer(
	({
		labelingModeViewStore: {
			activeStep,
			returnToPreviousStep,
			closeBox,
			printLabel,
			printPackingSlip,
			hasPackingSlip,
			hasUpcLabel,
			hasGs1Label,
			printStoredUpcLabel,
			printStoredGs1Label,
			steps: { dimensionsStep },
			loading,
			showCreateTemplate,
			dimensionsStepErrorMessage,
			closeBoxDisabled,
			onUndo,
			openAbortConfirmModal,
			boxedFulfillmentOrders,
			selectedFulfillmentOrder,
			isLtlLabeling,
			isLtlPackingMode,
			selectedFoHasUploadedBol,
			isLtlPalletsReconciled,
		},
		t,
	}) => {
		const pallets = dimensionsStep.form.$('pallets');
		// An uploaded BOL is reprinted as-is (no pallets, no dispatch), so the step is always valid. For a packed LTL
		// order every pallet's fields and its contents must be valid, and the per-product split must reconcile to the
		// order quantities.
		const isDimensionsStepValid = selectedFoHasUploadedBol
			? true
			: isLtlLabeling
			? pallets.value.length > 0 &&
			  isLtlPalletsReconciled &&
			  pallets
					.map(
						pallet =>
							pallet.$('palletLength').isValid &&
							pallet.$('palletWidth').isValid &&
							pallet.$('palletHeight').isValid &&
							pallet.$('palletWeight').isValid &&
							pallet.$('palletCount').isValid &&
							pallet
								.$('contents')
								.map(
									content =>
										content.$('fulfillmentOrderItemId').isValid &&
										content.$('quantity').isValid
								)
								.every(Boolean)
					)
					.every(Boolean)
			: dimensionsStep.form.$('boxId').isValid;
		// LTL packing has nothing to abort: the FO only changes on Print BOL, which also closes the modal.
		const isAbortVisible =
			!isLtlPackingMode && (activeStep.number >= 3 || (activeStep.number === 2 && boxedFulfillmentOrders.length > 0));
		return (
			<div className="labeling__footer">
				{/* UNDO BUTTON */}
				{/* Visible only step 4 (Palletizing step) */}
				{activeStep.number === 4 && (
					<button
						className="btn btn--med btn--outline"
						type="button"
						onClick={onUndo}
						disabled={selectedFulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.exitScan}
					>
						{t('FULFILLMENTORDER.LABELINGMODE.UNDO')}
					</button>
				)}
				{/* BACK BUTTON AND CLOSE BOX BUTTON */}
				{/* Visible only on step 2 (Labeling step) */}
				{activeStep.number === 2 && (
					<Fragment>
						<button className="btn btn--med btn--outline" type="button" onClick={() => returnToPreviousStep()}>
							{t('BACK')}
						</button>
						<button
							className="btn btn--med btn--primary"
							type="button"
							disabled={closeBoxDisabled}
							onClick={() => closeBox()}
						>
							{t('FULFILLMENTORDER.LABELINGMODE.LABELINGSTEP.CLOSEBOX')}
						</button>
					</Fragment>
				)}
				{/* PRINT PACKING SLIP BUTTON */}
				{/* Visible only on step 3 and 4 (Dimenisons and Palletizing step), and only when a packing slip
				    exists - generic slips are no longer generated, so without one there is nothing to print. */}
				{(activeStep.number === 3 || activeStep.number === 4) && hasPackingSlip && (
					<button
						className="btn btn--med btn--outline"
						type="button"
						onClick={() => {
							printPackingSlip(true);
						}}
						disabled={dimensionsStepErrorMessage}
					>
						<i className="fa fa-print spc--right--tny"></i>
						{t('FULFILLMENTORDER.LABELINGMODE.PACKINGSLIP.PRINT')}
					</button>
				)}
				{/* PRINT UPC LABEL AND GS1 LABEL BUTTONS - same rule as the packing slip: steps 3 and 4, only when one exists */}
				{(activeStep.number === 3 || activeStep.number === 4) && hasUpcLabel && (
					<button
						className="btn btn--med btn--outline"
						type="button"
						onClick={() => printStoredUpcLabel()}
						disabled={dimensionsStepErrorMessage}
						data-cy="labelingModePrintUpcLabel"
					>
						<i className="fa fa-print spc--right--tny"></i>
						{t('PRINTUPCLABEL')}
					</button>
				)}
				{(activeStep.number === 3 || activeStep.number === 4) && hasGs1Label && (
					<button
						className="btn btn--med btn--outline"
						type="button"
						onClick={() => printStoredGs1Label()}
						disabled={dimensionsStepErrorMessage}
						data-cy="labelingModePrintGs1Label"
					>
						<i className="fa fa-print spc--right--tny"></i>
						{t('PRINTGS1LABEL')}
					</button>
				)}
				{/* PRINT LABEL BUTTON */}
				{/* Visible only on step 3 (Dimensions step) */}
				{activeStep.number === 3 && (
					<button
						className="btn btn--med btn--primary"
						type="button"
						disabled={
							!isDimensionsStepValid || loading || showCreateTemplate || dimensionsStepErrorMessage
						}
						onClick={() => printLabel()}
					>
						{loading && (
							<div className="spinnercont spc--right--sml">
								<div className="spinner"></div>
							</div>
						)}
						<i className="fa fa-print spc--right--tny"></i>
						{isLtlLabeling || selectedFulfillmentOrder.carrierService.name.toLowerCase() == 'ltl'
							? t('PRINTBOL')
							: t('FULFILLMENTORDER.LABELINGMODE.PRINTLABEL')}
					</button>
				)}
				{/* ABORT BUTTON */}
				{/* Visible on steps 3, 4 and 2 if order was split, except in LTL packing mode */}
				{isAbortVisible && (
					<div className="flex--grow--1 type--right">
						<button
							className="btn btn--med btn--warning"
							type="button"
							onClick={() => openAbortConfirmModal()}
							disabled={selectedFulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.exitScan}
						>
							{t('FULFILLMENTORDER.LABELINGMODE.ABORT')}
						</button>
					</div>
				)}
			</div>
		);
	}
);

const Header = observer(({ steps, t, labelingModeViewStore }) => {
	const {
		palletDropdownStore,
		useRegularPalletizingMode,
		activeStep,
		steps: { labelingStep },
		setInputs,
		onUpcScan,
		isLtlPackingMode,
		onSearchChange,
	} = labelingModeViewStore;
	return (
		<Fragment>
			<div className="row">
				{!useRegularPalletizingMode && (
					<Fragment>
						<div className="col col-sml-3 col-med-3">
							<p>{t('FULFILLMENTORDER.LABELINGMODE.PALLET')}</p>
							{palletDropdownStore.value && <p>{palletDropdownStore.value.label}</p>}
						</div>
						{activeStep.number == 1 || activeStep.number == 2 ? (
							<div className="col col-sml-5 col-med-5">
								{!isLtlPackingMode && (
									<BaasicInput
										field={labelingStep.form.$('upc')}
										reference={setInputs}
										onKeyUp={onUpcScan}
										disabled={!useRegularPalletizingMode && palletDropdownStore.value == null}
									/>
								)}
							</div>
						) : (
							!isLtlPackingMode && (
								<div className="col col-sml-5 col-med-5">
									<p>{t('FULFILLMENTORDER.LABELINGMODE.UPC')}</p>
									<p>{labelingStep.form.$('upc').value}</p>
								</div>
							)
						)}
					</Fragment>
				)}
			</div>
			{/* LTL packing is a single screen once the FO is entered, so there are no steps to show */}
			{!isLtlPackingMode && (
				<div className="labeling__step">
					{_.map(steps, step => {
						return (
							<div className={'labeling__step__item'} key={`${step.order}-${step.title}`}>
								<div
									className={
										`labeling__step__number ` + (step.isCompleted ? 'is-completed' : step.isActive ? 'is-active' : '')
									}
								>
									{step.number}
								</div>
								<div className={'labeling__step__label'}>{t(step.title)}</div>
							</div>
						);
					})}
				</div>
			)}
		</Fragment>
	);
});

export default defaultTemplate(LabelingModeTemplate);
